import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import withIdleTimer from "../common/WithIdleTimer";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import Footer from "../shared/Footer/Footer";
import { logMsg } from "../common/Logger";
import { ofType } from "../common/PubSub";
import PageError from "../errors/PageError";
import "../styles/sharedStyles.scss";
import styles from "./App.module.scss";
import { useTranslation } from "react-i18next";
import LocalizedInfoClient from "../clients/LocalizedInfoClient";
import NADealer from "./pure/NADealer";
import { ModalProvider } from "../shared/components/EMPModal/ModalContext";
import { PAGE, SALESCODE_KEY } from "../common/Constants";
import { UserContext } from "../shared/context/userContext";
import { useChangeBrand } from "./hooks/useChangeBrand";
import { fetchDealerGroup } from "../shared/hooks/useDealerGroup";
import OnboardingModalHandler from "../components/dealerOnboarding/OnboardingModalHandler/OnboardingModalHandler";
import DealerOnboardingLandingPage from "../components/dealerOnboarding/DealerOnboardingLandingPage/DealerOnboardingLandingPage";

/**
 * Plain object for UserContext — must match NADealer state after dealer group / brand updates.
 */
function buildUserContextFromNADealer(nadDealer, details) {
  if (!nadDealer) {
    return details ? { ...details } : {};
  }
  const d = details || {};
  return {
    ...d,
    market: nadDealer.market ?? d.market,
    brand: nadDealer.brand ?? d.brand,
    salesCode: nadDealer.salesCode ?? d.salesCode,
    dealerId: nadDealer.dealerId ?? d.dealerId,
    dealerName: nadDealer.dealerName ?? d.dealerName,
    commonId: nadDealer.commonId ?? d.commonId,
    commonDealerId: nadDealer.commonDealerId ?? d.commonDealerId,
    dealerType: nadDealer.dealerType ?? d.dealerType,
    userType: nadDealer.userType ?? d.userType,
    roles: nadDealer.roles ?? d.roles,
    permissions: nadDealer.permissions ?? d.permissions,
    enrolledSalesCodes: nadDealer.enrolledSalesCodes ?? d.enrolledSalesCodes,
    dealerGroup: nadDealer.dealerGroup ?? d.dealerGroup,
    dealerGroupId: nadDealer.dealerGroupId ?? d.dealerGroupId,
    fullName: d.fullName ?? nadDealer.fullName,
    userId: d.userId ?? nadDealer.userId,
    loggedInDealerId: nadDealer.loggedInDealerId ?? d.loggedInDealerId,
    commonName: nadDealer.commonName ?? d.commonName,
    dealerBrand: nadDealer.dealerBrand ?? d.dealerBrand,
  };
}

const EcommDealerOnboardingApp = ({
  history,
  userDetails,
  hideOrShow,
  i18n: i18nProp,
}) => {
  const getBrandSlug = useCallback((brandCode) => {
    return String(brandCode || "").toUpperCase() === "L" ? "lincoln" : "ford";
  }, []);

  const getMarketSlug = useCallback((marketCode) => {
    const market = String(marketCode || "").toUpperCase();
    if (market === "CAN" || market === "CANADA") return "canada";
    return "usa";
  }, []);

  const { t, i18n } = useTranslation();
  const { updateUser } = useContext(UserContext);

  // Use prop i18n if provided, otherwise use hook i18n
  const i18nInstance = i18nProp || i18n;

  const [renderApp, setRenderApp] = useState(false);
  const [user, setUser] = useState(() => new NADealer(userDetails, history));
  const [dealerGroupError, setDealerGroupError] = useState(false);
  const [canRender, setCanRender] = useState(false);
  const onboardingIdentifier = useMemo(
    () => `${getMarketSlug(user?.market)}-${getBrandSlug(user?.brand)}`,
    [getBrandSlug, getMarketSlug, user?.brand, user?.market]
  );
  const onboardingRoute = `/ecomm-onboarding/${onboardingIdentifier}`;

  const salesCodeRef = useRef(sessionStorage.getItem(SALESCODE_KEY));
  const localizedInfoRef = useRef({});
  const userRef = useRef(user);
  const initializedRef = useRef(false);

  // Keep userRef in sync with user state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Keep UserContext aligned with NADealer `user` (commonId, dealerGroup, token, etc.)
  useEffect(() => {
    if (!updateUser) return;
    updateUser(buildUserContextFromNADealer(user, userDetails));
  }, [
    updateUser,
    user,
    userDetails,
    renderApp,
    dealerGroupError,
    onboardingIdentifier,
  ]);

  const updateCurrentDealer = useCallback(
    (dealer, showAll = false) => {
      setUser((prevUser) => {
        const updatedUser = prevUser.updateCurrentDealer(dealer, showAll);
        if (i18nInstance?.language) {
          updatedUser.setLanguageCode(i18nInstance.language);
        }
        return updatedUser;
      });
    },
    [i18nInstance]
  );

  const updateCurrentDealerRef = useRef();
  updateCurrentDealerRef.current = updateCurrentDealer;

  const changeBrand = useChangeBrand({
    userRef,
    setUser,
    i18n: i18nInstance,
  });

  useEffect(() => {
    const subscription = ofType("switchOnboardingBrand").subscribe(
      ({ data: brandCode }) => {
        if (brandCode === "F" || brandCode === "L") {
          changeBrand(brandCode);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [changeBrand]);

  const getDealerGroup = useCallback(() => {
    const currentUser = userRef.current;
    hideOrShow(true);
    fetchDealerGroup(
      currentUser,
      salesCodeRef.current,
      sessionStorage.getItem(PAGE)
    )
      .then(({ data, selectedDealer }) => {
        currentUser.setDealerGroup(data);
        if (selectedDealer) {
          sessionStorage.setItem("dealerName", selectedDealer.dealerName);
        } else if (data?.[0]) {
          sessionStorage.setItem("dealerName", data[0].dealerName);
        }
        if (updateCurrentDealerRef.current && selectedDealer) {
          updateCurrentDealerRef.current(selectedDealer, true);
        }
        setRenderApp(true);
        hideOrShow(false);
      })
      .catch((error) => {
        setDealerGroupError(true);
        console.log(error);
        setRenderApp(true);
      })
      .finally(() => {
        hideOrShow(false);
      });
  }, [hideOrShow]);

  const getDealerGroupRef = useRef();
  getDealerGroupRef.current = getDealerGroup;

  const getLocalizedInfo = useCallback(() => {
    const currentUser = userRef.current;
    LocalizedInfoClient.getLocalizedInfo(currentUser)
      .then((response) => {
        localizedInfoRef.current = {
          ...localizedInfoRef.current,
          ...response,
        };
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        if (getDealerGroupRef.current) {
          getDealerGroupRef.current();
        }
      });
  }, []);

  // Initialize only once on mount
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      getLocalizedInfo();
    }
  }, []);

  // Wait for user to have salesCode before rendering (unless there's an error)
  useEffect(() => {
    if (renderApp && (dealerGroupError || user?.salesCode)) {
      setCanRender(true);
    }
  }, [renderApp, dealerGroupError, user]);

  useEffect(() => {
    const path = history?.location?.pathname || "";
    if (!path || path === "/error") return;
    if (path === onboardingRoute) return;

    // ecomm_onboarding users only have one valid destination. Redirect any
    // other path (including legacy bookmarks like /dashboard, /pricing, etc.
    // that MSAL may restore after login) to the onboarding landing page.
    history.replace(onboardingRoute);
  }, [history, onboardingRoute]);

  if (!renderApp || !canRender) {
    return "";
  }

  // Additional safety check
  if (!dealerGroupError && !user?.salesCode) {
    return "";
  }

  const handleBrandSwitchModalButtonClick = (buttonId, closeModal) => {
    if (buttonId.startsWith("switch-")) {
      const brandToSwitch = buttonId.includes("ford") ? "F" : "L";

      changeBrand(brandToSwitch === "F" ? "F" : "L");
      if (closeModal) {
        closeModal();
      }
    } else if (buttonId.startsWith("continue-")) {
      if (closeModal) {
        closeModal();
      }
    }
  };

  const globalPathname = history?.location?.pathname || "";

  return (
    <Router history={history}>
      <ModalProvider>
        <OnboardingModalHandler
          user={user}
          canRender={canRender}
          handleBrandSwitchModalButtonClick={handleBrandSwitchModalButtonClick}
        />
        <section className={styles.fullHeightLayout}>
          <div className={styles.fullHeightItem}>
            {!dealerGroupError && (
              <GlobalNavBar
                history={history}
                urlPath={globalPathname}
                user={user}
                i18n={i18nInstance}
                roles={user.roles}
                hideToolbar={true}
              />
            )}
          </div>
          <div className={styles.fullHeightItem}>
            <Switch>
              <Route
                exact
                path="/ecomm-onboarding/:identifier"
                component={withIdleTimer(DealerOnboardingLandingPage)}
              />
              <Route
                exact
                path="/"
                render={() => <Redirect to={onboardingRoute} />}
              />
              <Route
                exact
                path="/ecomm-onboarding"
                render={() => <Redirect to={onboardingRoute} />}
              />
              <Route
                exact
                path="/:identifier(usa-ford|usa-lincoln|canada-ford|canada-lincoln)"
                render={({ match }) => (
                  <Redirect
                    to={`/ecomm-onboarding/${match.params.identifier}`}
                  />
                )}
              />
              <Route
                exact
                path="/error"
                render={() => (
                  <PageError
                    code="Something went wrong"
                    body="We couldn't load your information. Please try again."
                  />
                )}
              />
              <Route render={() => <Redirect to={onboardingRoute} />} />
            </Switch>
          </div>
          <div className={styles.fullHeightItem}>
            {!dealerGroupError && (
              <span className="noPrint">
                <Footer
                  user={user}
                  footerTitle={t("Footer.baseTitle")}
                  helpSupportTitle={t("Footer.help")}
                  permissions={user?.permissions || {}}
                  hideUpcomingChanges={true}
                  showBrandingLogo={false}
                />
              </span>
            )}
          </div>
        </section>
      </ModalProvider>
    </Router>
  );
};

EcommDealerOnboardingApp.propTypes = {
  history: PropTypes.object.isRequired,
  userDetails: PropTypes.object.isRequired,
  hideOrShow: PropTypes.func.isRequired,
  i18n: PropTypes.object,
};

export default EcommDealerOnboardingApp;
